import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { Card, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { MapContainer, TileLayer, Popup, CircleMarker } from "react-leaflet";
import { getMapData } from "../../services/dashboardService";
import {
  getDistrict,
  getSubdistrictByDistrict,
} from "../../services/commonService";
import "leaflet/dist/leaflet.css";

// Fix for default markers in react-leaflet
import L from "leaflet";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const baseLayers = {
  osm: {
    name: "🌍 OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  satellite: {
    name: "🛰️ Esri Satelit",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
  hybrid: {
    name: "🛰️+🛣️ Esri Hybrid",
    type: "hybrid", // gabungan 2 TileLayer
  },
};

const MapView = () => {
  const [mapCenter, setMapCenter] = useState([4.1652, 126.778]); // Talaud coordinates
  const [zoom, setZoom] = useState(9);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSubdistrict, setSelectedSubdistrict] = useState("");
  // const [userLocation, setUserLocation] = useState(null);
  const [activeBase, setActiveBase] = useState("hybrid");

  const {
    register,
    // handleSubmit,
    // formState: { errors },
    setValue,
    // getValues,
  } = useForm();

  // Effect pertama: ambil data districts saat load awal
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const data = await getDistrict();
        setDistricts(data.data);
      } catch (error) {
        console.error("Gagal ambil district:", error);
      }
    };
    fetchDistricts();
  }, []);

  // mapping Kecamatan
  const districtMap = Object.fromEntries(districts.map((d) => [d.id, d.name]));

  // Effect kedua: ambil subdistrict tiap kali district berubah
  useEffect(() => {
    const fetchSubdistricts = async () => {
      if (selectedDistrict) {
        // console.log("DISTRICTS: ", districts);
        // console.log("SELECTEDDISTRICT: ", selectedDistrict);

        try {
          const data = await getSubdistrictByDistrict(selectedDistrict);
          setSubdistricts(data.data);
          setSelectedSubdistrict("");
        } catch (error) {
          console.error("Gagal ambil subdistrict:", error);
        }
      } else {
        setSubdistricts([]);
        // setValue("subdistrict_id", "");
        setSelectedSubdistrict("");
      }
    };
    fetchSubdistricts();
  }, [districts, selectedDistrict, setValue]);

  const {
    data: mapData,
    isLoading,
    error,
  } = useQuery(
    ["mapData", selectedDistrict, selectedSubdistrict],
    () => getMapData(selectedDistrict, selectedSubdistrict),
    { keepPreviousData: true }
  );

  const getMarkerColor = (score) => {
    if (score >= 80) return "#28a745"; // Green for good
    if (score >= 60) return "#ffc107"; // Yellow for moderate
    return "#dc3545"; // Red for poor
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Layak Huni";
    if (score >= 60) return "Kurang Layak Huni";
    return "Tidak Layak Huni";
  };

  const handleMarkerClick = (housing) => {
    // You can add custom logic here for marker clicks
    // console.log("Marker clicked:", housing);
  };

  if (isLoading) return <div className="loading-spinner">Loading peta...</div>;
  if (error) return <div className="error-message">Error: {error.message}</div>;

  return (
    <form>
      <div className="container-fluid">
        <div className="map-header d-flex flex-column flex-md-row mb-4 mt-2">
          <h2 className="w-50">Peta Rumah</h2>
          {/* Filter Kecamatan & Desa */}
          <Card className="w-100 ms-auto">
            <Card.Body className="d-flex flex-column flex-md-row gap-2">
              <Form.Group className="d-flex flex-md-row justify-content-between align-items-center gap-3 me-2">
                <Form.Label>Kecamatan</Form.Label>
                <Form.Select
                  {...register("district_id")}
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <option value="">SEMUA</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="d-flex flex-row justify-content-between align-items-center gap-2 me-2 ">
                <Form.Label>Desa/Kelurahan</Form.Label>
                <Form.Select
                  {...register("sub_district_id")}
                  value={selectedSubdistrict}
                  onChange={(e) => setSelectedSubdistrict(e.target.value)}
                  disabled={!selectedDistrict}
                >
                  <option value="">
                    {selectedDistrict
                      ? "SE-KECAMATAN " +
                        districtMap[selectedDistrict]
                      : "SEMUA"}
                  </option>
                  {subdistricts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </div>

        <Row>
          <Col
            xs={12}
            md={3}
            className="order-2 order-md-2 mb-3 mb-md-0 d-flex flex-column"
          >
            {/* Statistik */}
            <Card className="mb-3">
              <Card.Header>
                <h6>Statistik</h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <small className="text-muted">Total Data:</small>
                  <br />
                  <strong>{mapData?.length || 0} rumah</strong>
                </div>
                {mapData && mapData.length > 0 && (
                  <>
                    <div className="mb-2">
                      <small className="text-muted">Dengan Koordinat:</small>
                      <br />
                      <strong>
                        {
                          mapData.filter((h) => h.latitude && h.longitude)
                            .length
                        }{" "}
                        rumah
                      </strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Dengan Penilaian:</small>
                      <br />
                      <strong>
                        {mapData.filter((h) => h.assessment_score).length} rumah
                      </strong>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
            {/* Kontrol Peta */}
            <Card className="mb-3">
              <Card.Header>
                <h6>Kontrol Peta</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setMapCenter([3.5952, 126.978]);
                      setZoom(26);
                    }}
                  >
                    Reset View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={() => {
                      if (mapData && mapData.length > 0) {
                        const coords = mapData
                          .filter((h) => h.latitude && h.longitude)
                          .map((h) => [
                            parseFloat(h.latitude),
                            parseFloat(h.longitude),
                          ]);
                        if (coords.length > 0) {
                          // TODO: fit bounds ke map instance
                        }
                      }
                    }}
                  >
                    Fit All Markers
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Legenda */}
            <Card className="mb-3">
              <Card.Header>
                <h5>Legenda</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#28a745",
                        borderRadius: "50%",
                        marginRight: "8px",
                      }}
                    ></div>
                    <span>Layak Huni (≥80)</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#ffc107",
                        borderRadius: "50%",
                        marginRight: "8px",
                      }}
                    ></div>
                    <span>Kurang Layak Huni (60-79)</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#dc3545",
                        borderRadius: "50%",
                        marginRight: "8px",
                      }}
                    ></div>
                    <span>Tidak Layak Huni (&lt;60)</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          {/* Peta */}
          <Col xs={12} md={9} className="order-1 order-md-1">
            <Card className="mb-3">
              <Card.Body style={{ padding: 0 }}>
                <div style={{ height: "600px", width: "100%" }}>
                  <div
                    className="ms-auto"
                    style={{
                      justifySelf: "right",
                      right: "0",
                      border: "none",
                    }}
                  >
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "180px" }}
                      value={activeBase}
                      onChange={(e) => setActiveBase(e.target.value)}
                    >
                      {Object.entries(baseLayers).map(([key, layer]) => (
                        <option key={key} value={key}>
                          {layer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <MapContainer
                    center={mapCenter}
                    zoom={zoom}
                    style={{ height: "95%", width: "100%", zIndex: 0 }}
                  >
                    {activeBase === "hybrid" ? (
                      <>
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          attribution="© Esri"
                        />
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                          attribution="© Esri"
                        />
                      </>
                    ) : (
                      <TileLayer
                        url={baseLayers[activeBase].url}
                        attribution={baseLayers[activeBase].attribution}
                      />
                    )}
                    {mapData?.map((housing) => {
                      if (!housing.latitude || !housing.longitude) return null;

                      const score = housing.assessment_score || 0;
                      const color = getMarkerColor(score);

                      return (
                        <CircleMarker
                          key={housing.housingId}
                          center={[
                            parseFloat(housing.latitude),
                            parseFloat(housing.longitude),
                          ]}
                          radius={8}
                          pathOptions={{
                            color: color,
                            fillColor: color,
                            fillOpacity: 0.7,
                          }}
                          eventHandlers={{
                            click: () => handleMarkerClick(housing),
                          }}
                        >
                          <Popup>
                            <div style={{ minWidth: "200px" }}>
                              <h6>BR-{housing.housingId}</h6>
                              <p>
                                <strong>Kecamatan:</strong>{" "}
                                {housing.district_name}
                              </p>
                              <p>
                                <strong>Desa/Kelurahan:</strong>{" "}
                                {housing.subdistrict_name}
                              </p>
                              <p>
                                <strong>Jenis Bangunan:</strong>{" "}
                                {housing.building_type}
                              </p>
                              <p>
                                <strong>Luas Bangunan:</strong>{" "}
                                {housing.house_area_m2} m²
                              </p>
                              {score > 0 && (
                                <div>
                                  <p>
                                    <strong>Skor Kelayakan:</strong>
                                  </p>
                                  <Badge
                                    bg={
                                      score >= 80
                                        ? "success"
                                        : score >= 60
                                        ? "warning"
                                        : "danger"
                                    }
                                  >
                                    {score.toFixed(1)} - {getScoreLabel(score)}
                                  </Badge>
                                </div>
                              )}
                              <div className="mt-2 d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() =>
                                    window.open(
                                      `/housing/${housing.housingId}`,
                                      "_blank"
                                    )
                                  }
                                >
                                  Lihat Detail
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => {
                                    const lat = housing.latitude;
                                    const lng = housing.longitude;
                                    if (lat && lng) {
                                      const url = `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},18z/data=!3m1!1e3`;
                                      window.open(url, "_blank");
                                    }
                                  }}
                                >
                                  <i className="fas fa-map-marker-alt"></i>
                                </Button>
                              </div>
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </form>
  );
};

export default MapView;
